// src/components/PurchaseFlow/PurchaseFlow.js

import React, { useState, useEffect, useContext, useRef } from 'react';
import { ClientContext } from '../../context/ClientContext';
import { SelectedOfferProvider } from '../../context/SelectedOfferContext';
import { SuggestedDataProvider } from '../../context/SuggestedDataContext';

// Importar todos los componentes de pasos
import OfferStep from './Steps/Offer/OfferStep';
import DataEntryStep from './Steps/DataEntry/DataEntryStep';
import CustomerHandoffStep from './Steps/CustomerHandoff/CustomerHandoffStep';
import DocumentUploadStep from './Steps/DocumentUpload/DocumentUploadStep';
import ConfirmationStep from './Steps/ConfirmationStep';
import PaymentStep from './Steps/Payment/PaymentStep';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { trackSaleStarted, trackSaleAbandoned, trackSaleStepEntered } from '../../analytics/events';
import { useAnalyticsContext } from '../../analytics/useAnalyticsContext';

import './PurchaseFlow.css';

const PurchaseFlow = ({ onClose }) => {
  const { clientData, clientStatus } = useContext(ClientContext);
  const { commerceId, branchId, channel } = useAnalyticsContext();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [steps, setSteps] = useState([]);
  const [stepLabels, setStepsLabels] = useState([]);
  const [purchaseRequestId, setPurchaseRequestId] = useState(null);
  const [purchaseRequestUid, setPurchaseRequestUid] = useState(null);
  const [lastStepStatus, setLastStepStatus] = useState('pending');
  const lastStepStatusRef = useRef('pending');

  const [formData, setFormData] = useState({});
  const saleStartedTrackedRef = useRef(false);

  const clientPurchaseFlowMap = {
    NEW: process.env.REACT_APP_PURCHASE_FLOW_NEW,
    RENEWER: process.env.REACT_APP_PURCHASE_FLOW_RENEWER,
    OLD: process.env.REACT_APP_PURCHASE_FLOW_OLD,
    RESTRICTED: process.env.REACT_APP_PURCHASE_FLOW_RESTRICTED,
    BLACKLISTED: process.env.REACT_APP_PURCHASE_FLOW_BLACKLISTED
  };  

  // Track sale started when PurchaseFlow mounts (only once per flow instance)
  useEffect(() => {
    if (commerceId && clientData?.id && !saleStartedTrackedRef.current) {
      trackSaleStarted({
        commerceId,
        branchId,
        availableCredit: clientData.available_credit || 0,
        currency: 'ARS',
        customerDni: clientData?.dni
      });
      saleStartedTrackedRef.current = true;
    }
  }, [commerceId, branchId, clientData?.id, clientData?.dni]);

  // Track sale abandoned when PurchaseFlow closes without completion
  // Note: This will fire on unmount, but we rely on lastStepStatusRef to determine
  // if the sale was completed. If lastStepStatusRef.current is 'success', the sale was completed.
  useEffect(() => {
    return () => {
      // Only track abandonment if the sale wasn't completed
      // This cleanup runs when component unmounts (flow closed)
      if (commerceId && lastStepStatusRef.current !== 'success') {
        trackSaleAbandoned({
          commerceId,
          branchId,
          reason: 'flow_closed',
          customerDni: clientData?.dni
        });
      }
    };
  }, [commerceId, branchId]);

  useEffect(() => {
    const getStepsForClientStatus = () => {
      let stepsConfig = '';
      stepsConfig = clientPurchaseFlowMap[clientStatus];

      if (!stepsConfig) {
        console.error(`No hay configuración de pasos para el estado ${clientStatus}`);
        return;
      }

      const stepsArray = stepsConfig.split(',').map((stepName) => {
        const trimmedStepName = stepName.trim();
        switch (trimmedStepName) {
          case 'OfferStep':
            return OfferStep;
          case 'DataEntryStep':
            return DataEntryStep;
          case 'CustomerHandoffStep':
            return CustomerHandoffStep;
          case 'DocumentUploadStep':
            return DocumentUploadStep;
          case 'ConfirmationStep':
            return ConfirmationStep;
          case 'PaymentStep':
            return PaymentStep;
          default:
            console.warn(`Paso desconocido: "${trimmedStepName}"`);
            return null;
        }
      });

      const stepLabelsArray = stepsConfig.split(',').map((stepName) => {
        const trimmedStepName = stepName.trim();
        switch (trimmedStepName) {
          case 'OfferStep':
            return 'Producto';
          case 'DataEntryStep':
            return 'Datos Personales';
          case 'CustomerHandoffStep':
            return 'Autogestión';
          case 'DocumentUploadStep':
            return 'Documentos';
          case 'ConfirmationStep':
            return 'Confirmación';
          case 'PaymentStep':
            return 'Medio de Pago';
          default:
            console.warn(`Etiqueta desconocida para paso: "${trimmedStepName}"`);
            return null;
        }
      });

      setSteps(stepsArray);
      setStepsLabels(stepLabelsArray);
    };

    getStepsForClientStatus();

  }, [clientStatus]);

  const handleNext = (data, stepStatus = null) => {
    setFormData((prevData) => {
      const updatedData = {
        ...prevData,
        ...data,
      };
      return updatedData;
    });

    if (currentStepIndex === steps.length - 1 && stepStatus) {
      setLastStepStatus(stepStatus);
      lastStepStatusRef.current = stepStatus;
    } else {
      setCurrentStepIndex((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStepIndex((prevStep) => prevStep - 1);
  };

  // Track step entered
  useEffect(() => {
    if (commerceId && steps.length > 0 && currentStepIndex < steps.length) {
      const stepName = stepLabels[currentStepIndex] || `Step${currentStepIndex + 1}`;
      // Map step labels to step names
      const stepNameMap = {
        'Producto': 'OfferStep',
        'Datos Personales': 'DataEntryStep',
        'Autogestión': 'CustomerHandoffStep',
        'Documentos': 'DocumentUploadStep',
        'Confirmación': 'ConfirmationStep',
        'Medio de Pago': 'PaymentStep'
      };
      const mappedStepName = stepNameMap[stepName] || stepName;
      
      trackSaleStepEntered({
        commerceId,
        branchId,
        stepName: mappedStepName,
        customerDni: clientData?.dni
      });
    }
  }, [commerceId, branchId, currentStepIndex, steps.length, stepLabels, clientData?.dni]);

  const renderStep = () => {
    const CurrentStepComponent = steps[currentStepIndex];

    if (!CurrentStepComponent) {
      console.error(`Componente para el paso index ${currentStepIndex} no está definido.`);
      return <div>Error al cargar el paso. Por favor, intenta nuevamente.</div>;
    }

    return (
      <CurrentStepComponent
        clientData={clientData}
        purchaseRequestId={purchaseRequestId}
        purchaseRequestUid={purchaseRequestUid}
        setPurchaseRequestId={setPurchaseRequestId}
        setPurchaseRequestUid={setPurchaseRequestUid}
        onNext={handleNext}
        onBack={handleBack}
        formData={formData}
      />
    );
  };

  // Función para calcular el progreso
  const calculateProgress = () => {
    const totalSteps = steps.length;
    if (totalSteps === 0) return 0;

    // Si el último paso está en success, retornamos 100%
    if (currentStepIndex === totalSteps - 1 && lastStepStatus === 'success') {
      return 100;
    }

    // Calculamos el progreso basado en los pasos completados
    const completedSteps = currentStepIndex;
    return (completedSteps / (totalSteps - 1)) * 100;
  };

  return (
    <SelectedOfferProvider>
      <SuggestedDataProvider>
        <div className="purchase-flow">

          {/* Stepper con barra de progreso - solo visible si no está confirmado */}
          {!(currentStepIndex === steps.length - 1 && lastStepStatus === 'success') && (
            <div className="stepper-container mb-4">
              <div className="stepper-wrapper">
                {steps.map((_, index) => {
                  let stepClassName = 'stepper-item';
                  if (index === currentStepIndex) {
                    if (index === steps.length - 1 && lastStepStatus === 'success') {
                      stepClassName += ' completed';
                    } else {
                      stepClassName += ' active';
                    }
                  } else if (index < currentStepIndex) {
                    stepClassName += ' completed';
                  }

                  return (
                    <div className={stepClassName} key={index}>
                      <div className="step-counter">
                        {(index < currentStepIndex || 
                          (index === steps.length - 1 && lastStepStatus === 'success')) ? (
                          <FontAwesomeIcon icon={faCheck} />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <p className="step-label">{stepLabels[index]}</p>
                    </div>
                  );
                })}
              </div>
              {/* Barra de progreso actualizada */}
              <div className="progress-bar-wrapper">
                <div
                  className="progress-bar"
                  style={{
                    width: `${calculateProgress()}%`,
                    transition: 'width 0.3s ease-in-out' // Agregamos una transición suave
                  }}
                ></div>
              </div>
            </div>
          )}

          {/* Renderizar el componente del paso actual */}
          {renderStep()}
        </div>
      </SuggestedDataProvider>
    </SelectedOfferProvider>
  );
};

export default PurchaseFlow;
